import { useMemo, useState } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import { Criterion, ParticipantField, ParticipantFieldType } from '@app/api';
import { CheckboxesToArrayField } from '@app/form';
import { IconChip } from '@app/core';
import { Slider } from 'components';
import { getCriterionValuesTextArray } from './Criteria';

const CHECKBOX_CHOICES_MAX = 3;

const VISIBLE_CHIPS = 10;

interface Props {
  field: ParticipantField;
  criterion: Criterion;
}

const useStyles = createUseStyles((theme: Theme) => ({
  readOnly: {
    pointerEvents: 'none',
    userSelect: 'none',
  },
  range: {
    paddingLeft: theme.marginBase,
    paddingRight: theme.marginBase,
    '& .MuiSlider-valueLabel': {
      backgroundColor: theme.colors.inkPrimary,
      borderRadius: theme.borderRadius.std,
    },
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  chip: {
    borderRadius: theme.height.chips / 2,
  },
  more: {
    ...theme.fonts.caption,
    color: theme.colors.blueAccessible,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    paddingBlock: theme.marginBase / 2,
    paddingInline: theme.marginBase,
  },
  moreIcon: {
    fontSize: theme.iconSize.small,
    marginLeft: theme.marginBase / 2,
    verticalAlign: 'middle',
  },
}));

const selectedValues = (criterion: Criterion): string[] =>
  (criterion.allowList || []).map(String);

const RangePreview = ({ field, criterion }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { min, max, step } = field.range || {};

  const value = useMemo(() => {
    const selected = criterion.allowList?.[0];
    if (!selected || typeof selected !== 'object' || 'location' in selected) {
      return [min, max];
    }
    return [selected.min ?? min, selected.max ?? max];
  }, [criterion, min, max]);

  const marks = useMemo(() => {
    if (min === undefined || max === undefined || !step) {
      return undefined;
    }
    const start = min % step === 0 ? min + step : min + (step - (min % step));
    const marks = [{ value: min }];
    for (let i = start; i <= max; i += step) {
      marks.push({ value: i });
    }
    return marks;
  }, [min, max, step]);

  return (
    <div className={classnames(classes.readOnly, classes.range)}>
      <Slider
        min={min}
        max={max}
        step={null}
        marks={marks}
        value={value}
        components={{}}
      />
    </div>
  );
};

const ChoicesPreview = ({ field, criterion }: Props) => {
  const classes = useStyles();
  const values = selectedValues(criterion);

  return (
    <CheckboxesToArrayField
      name={field.name}
      options={(field.choices || []) as { label: string; value: string }[]}
      values={values}
      onChange={() => undefined}
      noMargin
      checkboxListClassName={classes.chips}
    />
  );
};

const ChipsPreview = ({ field, criterion }: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  const labels = useMemo(
    () => getCriterionValuesTextArray(criterion, field),
    [criterion, field],
  );
  const hidden = expanded ? 0 : Math.max(labels.length - VISIBLE_CHIPS, 0);

  return (
    <div className={classes.chips}>
      {(expanded ? labels : labels.slice(0, VISIBLE_CHIPS)).map((label) => (
        <IconChip
          key={label}
          className={classes.chip}
          color="green"
          label={label}
        />
      ))}
      {!!hidden && (
        <button
          type="button"
          className={classes.more}
          onClick={() => setExpanded(true)}
        >
          {t('editSession.targeting.iaSuggestion.more', { count: hidden })}
          <i className={classnames('ri-arrow-down-s-line', classes.moreIcon)} />
        </button>
      )}
    </div>
  );
};

/**
 * Shows a suggested criterion the way the targeting form renders it, so that the
 * designer reads the suggestion in the shape they will get once they accept it.
 */
export const TargetIaCriterionPreview = ({ field, criterion }: Props) => {
  const classes = useStyles();

  if (field.range && field.validation) {
    return <RangePreview field={field} criterion={criterion} />;
  }

  const isChoiceField =
    field.type === ParticipantFieldType.SINGLE_CHOICE ||
    field.type === ParticipantFieldType.MULTIPLE_CHOICE;

  if (isChoiceField && (field.choices || []).length <= CHECKBOX_CHOICES_MAX) {
    return (
      <div className={classes.readOnly}>
        <ChoicesPreview field={field} criterion={criterion} />
      </div>
    );
  }

  return <ChipsPreview field={field} criterion={criterion} />;
};
